import {
  getVariantAttributeLabel,
  nonEmpty,
  showPrice,
  subtract,
  type EnrichedSwarovskiProductVariantBaseFrontend,
  type ProductVariantAvailabilityStatus,
  type SwarovskiOptikSegmentShort,
} from "@swo/shared";
import {
  SwarovskiOptikLocaleFrontend,
  TypedMoney,
  type BundleTotalPriceResponse,
  type GeneralTranslation,
  type ProductDetailPageTranslation,
  type ProductDiscountT,
} from "@swo/storefront";
import { nonEmptyArray as NEA, option as O } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import type React from "react";
import { useCallback, useState } from "react";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { useGeneralTranslationsContext } from "../../../global/hooks/use-general-translations-context";
import { useCtxFns } from "../../../global/page-context";
import { BDPVariantPicker } from "./BDPVariantPicker";
import { PDPBundleContents } from "../PDPBundleContents";
import { PDPGalleryModalRoot } from "../PDPGalleryModalRoot";
import { BDPMetadata } from "../PDPMetadata";
import { PDPModal } from "../PDPModal";
import { ProductDetailPageDescription } from "../PDP_Description/ProductDetailPageDescription";
import { usePdpGalleryWithLightbox } from "../PDP_Lightbox/use-pdp-gallery-with-lightbox";
import { PDPProductDetailsIntro } from "../PDP_SingleImageCarousel/PDPAboveTheFoldLayout";
import { PDPAboveTheFoldLayoutWrapper } from "../PDP_SingleImageCarousel/PDPAboveTheFoldLayoutWrapper";
import { PDPBundleTeaser } from "../PDP_SingleImageCarousel/PDPBundleTeaser";
import { PDPDetailsWrapper } from "../PDP_SingleImageCarousel/PDPDetailsWrapper";
import { PDPImageWithPlaceholder } from "../PDP_SingleImageCarousel/PDPImageWithPlaceholder";
import { getGalleryProps } from "../PDP_SingleImageCarousel/PDPStickyGallery";
import { ProductDetailsIntro } from "../PDP_SingleImageCarousel/ProductDetailsIntro";
import { computePDPAvailabilityBarState } from "../PDP_SingleImageCarousel/availability/pdp-availability";
import { PDPStickyPriceBar } from "../PDP_SingleImageCarousel/price-bar/PDPStickyPriceBar";
import { getPDPStockLevelIndicator2 } from "../PDP_SingleImageCarousel/price-bar/PDPStockLevelIndicator";
import { getBundleSelectionParams } from "./getBundleSelectionParams";
import { getImages } from "../getImages";
import { getPDPActions } from "../getPDPActions";
import type { PageProps } from "../product-detail-page-layout";
import type { BundlePageProps } from "../getDefaultVariantsSelection";
import { outOfStock } from "../state/get-product-availability";
import { createAvailabilityMap, useCurrentVariantsSelection } from "../useCurrentVariantsSelection";
import { getPdpStickyDesktopButton } from "../PDP_SingleImageCarousel/price-bar/GetPdpStickyDesktopButton";
import { PDPScrollingGallery } from "../PDP_Gallery/PDPScrollingGallery";
import type { ReviewTranslations } from "../reviewTranslations";
import type { HeaderPinnedState } from "../../../components/HeaderComponent/subcomponents/use-header-pinned-state";

import { BDPBelowTheFoldLayout } from "./BDPBelowTheFoldLayout";
import type { PageContextType } from "../../../global/PageContextType";
import type { Translated } from "../../../domain/translations/translations";
import { InterpolateLinearlyReact } from "../../../domain/translations/interpolate-linearly-react";
import { T, TWrapper } from "../../../components/T";
import { hasDiscount } from "../../../domain/products";
import { storefrontClient } from "../../../domain/storefront";
import type { BundleVariantSelection } from "./BundleVariantSelection";
import { ReactComponent as InfoIcon } from "../../../assets/icons/info.svg";

import { useQuery } from "@tanstack/react-query";
import { css } from "@emotion/react";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { FONTS } from "../../../styles/fonts";
import { SwarovskiInfoTooltip } from "../../../components/_shared-components/ui-elements/SwarovskiTooltip";
import { SwarovskiPriceTag, SwarovskiTag } from "../../../components/_shared-components/ui-elements/SwarovskiTag";

export const BDPMainComponent: React.FC<{
  data: Omit<BundlePageProps["data"], "header" | "footer">;
  PdpInformationSet: React.ReactNode;
  ContactDataComponent: React.ReactNode;
  page: PageProps["page"];
  locale: SwarovskiOptikLocaleFrontend;
  segment: SwarovskiOptikSegmentShort;
  headerState: HeaderPinnedState;
  reviewsT: ReviewTranslations;
  pdpT: Translated<ProductDetailPageTranslation>;
  children?: React.ReactNode;
}> = ({
  data,
  PdpInformationSet,
  ContactDataComponent,
  page,
  locale,
  segment,
  headerState,
  reviewsT,
  pdpT,
  children,
}) => {
  const availabilityMap = createAvailabilityMap(data.availability);

  const { state: currentVariantsState, onBundleElementChanged } = useCurrentVariantsSelection(
    data.selectedProduct,
    availabilityMap,
  );

  const { availability } = getBundleSelectionParams(currentVariantsState, availabilityMap);

  function fetchBundleVariantPrice(currentVariantsState: BundleVariantSelection) {
    return storefrontClient.getBundleTotalPrice(
      locale,
      segment,
      data.selectedProduct.key,
      pipe(
        currentVariantsState,
        NEA.map(v => v.value.sku),
      ),
    );
  }

  const {
    data: price,
    isFetching: isPriceFetching,
    isError: isPriceError,
  } = useQuery<BundleTotalPriceResponse>({
    queryKey: ["bdp-price", JSON.stringify(currentVariantsState)],
    queryFn: async () => fetchBundleVariantPrice(currentVariantsState),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const [isModalOpen, setModalOpen] = useState(false);
  const hideModalCallback = useCallback(() => {
    setModalOpen(false);
  }, []);

  const galleryConfig = getGalleryProps(data.selectedProduct, null, 10);
  const galleryWithLightbox = usePdpGalleryWithLightbox(galleryConfig);

  const { addToCart: addToCart_, areCartActionsLocked } = getPDPActions(
    setModalOpen,
    data.selectedProduct.id,
    null,
    data.links,
  );

  const addToCart = () => addToCart_({ tag: "bdp", selectedVariants: currentVariantsState.map(v => v.value) });

  const availabilityState = computePDPAvailabilityBarState(
    { tag: "impossible" },
    availability ?? outOfStock(),
    addToCart,
    locale,
    data.translations.pdp,
    data.links,
    undefined,
    null,
  );

  const primaryButton = getPdpStickyDesktopButton(availabilityState.action, areCartActionsLocked);

  const deliveryPackageProps = pipe(data.selectedProduct.deliveryPackage, NEA.fromArray, O.toUndefined);
  const longDescription = data.selectedProduct.longDescription;

  const ctx = useCtxFns();
  const gt = useGeneralTranslationsContext();
  const discountablePriceFormatter = discountablePriceWithSavingNote(
    ctx,
    gt,
    pdpT,
    typeof price?.totalPrice.discounted !== "string" ? price?.totalPrice.discounted.discountPercentage : undefined,
    typeof price?.totalPrice.lowestPrice30Days !== "string" ? price?.totalPrice.lowestPrice30Days : undefined,
  );

  const bundleContents = currentVariantsState.map(({ value }) => {
    const attributesList = value.attributes.unique
      .map(a => {
        const label = getVariantAttributeLabel(
          a,
          {
            noLabel: data.translations.techSpec.noLabel.value,
            yesLabel: data.translations.techSpec.yesLabel.value,
          },
          locale.country === "INTERNATIONAL" ? "AT" : locale.country.isoCode,
        );

        return label
          ? {
              label,
              type: a.type === "color" ? ("color" as const) : ("plain" as const),
              customValue: a.type === "color" ? a.value?.hexValue : undefined,
            }
          : undefined;
      })
      .filter(nonEmpty);

    return {
      headline: value.product.name,
      attributes: attributesList,
      image: [...getImages(value.images), ...getImages(value.product.images)][0],
      link: value.path,
      price: value.price,
      shortDescription: value.product.shortDescription,
    };
  });

  const stickyPriceProps = isPriceFetching
    ? ("loading" as const)
    : isPriceError
      ? ("error" as const)
      : price
        ? discountablePriceFormatter({
            price: price.totalPrice.value,
            discountedPrice:
              typeof price.totalPrice.discounted !== "string"
                ? price.totalPrice.discounted.value
                : price.totalPrice.discounted,
          })
        : undefined;

  return (
    <>
      <BDPMetadata
        selectedProduct={data.selectedProduct}
        seoMetadata={data.seoMetadata}
        taxonomy={data.taxonomy}
        page={page}
      />

      <PDPModal isModalOpen={isModalOpen} hideModalCallback={hideModalCallback} />

      <PDPAboveTheFoldLayoutWrapper>
        <PDPImageWithPlaceholder
          galleryWithLightbox={galleryWithLightbox}
          availability={availability}
          isDiscounted={hasDiscount(data.selectedProduct.discountedStartingPrice)}
        />

        <PDPDetailsWrapper
          Element={
            <PDPStickyPriceBar
              primaryButton={primaryButton("normal")}
              extension={availabilityState.extension}
              price={stickyPriceProps}
              stockLevel={getPDPStockLevelIndicator2(availability)}
            />
          }
        >
          <ProductDetailsIntro
            breadcrumbs={data.taxonomy.breadcrumbs}
            isNew={data.selectedProduct.isNew}
            name={data.selectedProduct.name}
            SubheadlineElement={null}
          />

          <ProductDetailPageDescription productShortDescription={data.selectedProduct.shortDescription} />

          {availabilityMap !== "shopping-not-possible" ? (
            <BDPVariantPicker
              products={data.selectedProduct.products}
              currentVariantsState={currentVariantsState}
              onBundleElementChanged={onBundleElementChanged}
              countryIsoCode={locale.country === "INTERNATIONAL" ? "AT" : locale.country.isoCode}
              translations={data.translations.techSpec}
              priceLoadingError={data.translations.pdp.priceLoadingError}
              priceLabel={
                isPriceFetching
                  ? ("loading" as const)
                  : isPriceError
                    ? ("error" as const)
                    : price
                      ? {
                          regular: ctx.showPrice(price.totalPrice.value),
                          discounted:
                            typeof price.totalPrice.discounted !== "string"
                              ? ctx.showPrice(price.totalPrice.discounted.value)
                              : undefined,
                        }
                      : undefined
              }
              availabilityMap={availabilityMap}
            />
          ) : null}
        </PDPDetailsWrapper>

        <PDPProductDetailsIntro>
          {PdpInformationSet}
          <PDPBundleTeaser bundles={data.related.bundles} />
          <PDPScrollingGallery galleryWithLightbox={galleryWithLightbox} />
        </PDPProductDetailsIntro>

        <PDPGalleryModalRoot galleryWithLightbox={galleryWithLightbox} />
      </PDPAboveTheFoldLayoutWrapper>

      {/* <Spacer size={"lg"} /> */}

      <BDPBelowTheFoldLayout
        data={data}
        gt={gt}
        reviewsT={reviewsT}
        primaryButton={primaryButton("small")}
        headerState={headerState}
        deliveryPackageProps={deliveryPackageProps}
        longDescription={longDescription}
        BundleContents={<PDPBundleContents elements={bundleContents} />}
      />

      {children}

      <Spacer size={"lg"} />
      {ContactDataComponent}
    </>
  );
};

export const discountablePriceWithSavingNote = (
  ctx: PageContextType["contextFunctions"],
  gt: Translated<GeneralTranslation>,
  pdpT: Translated<ProductDetailPageTranslation>,
  discountPercentage: number | undefined,
  lowestPrice30Days: TypedMoney | undefined,
) => {
  const { showPrice, showDiscountablePriceStr } = ctx;

  return (variant: Pick<EnrichedSwarovskiProductVariantBaseFrontend, "price" | "discountedPrice">) => {
    function getNoteValue(): React.ReactNode | undefined {
      if (hasDiscount(variant.discountedPrice)) {
        const discountAmount = subtract(variant.price, variant.discountedPrice);
        const value = discountAmount.centAmount !== 0 ? showPrice(discountAmount) : undefined;
        const tagValue = `- ${value}`;
        return (
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <span
              css={css`
                margin-right: 4px;
                color: ${CSS_COLORS.GrayDark2024};
              `}
            >
              <TWrapper forcedValue={gt("beforePrice")}>
                <InterpolateLinearlyReact
                  translationStringWithInterpolatedValues={gt("beforePrice").value}
                  args={[showPrice(variant.price)]}
                />
              </TWrapper>
            </span>
            <SwarovskiPriceTag
              css={[
                css`
                  margin-right: 4px;
                `,
              ]}
              label={tagValue}
            >
              {tagValue}
            </SwarovskiPriceTag>
            <SwarovskiInfoTooltip
              content={
                <>
                  <TWrapper forcedValue={pdpT("bundleTooltip")}>
                    <InterpolateLinearlyReact
                      translationStringWithInterpolatedValues={pdpT("bundleTooltip").value}
                      args={[value]}
                    />
                  </TWrapper>

                  {typeof discountPercentage !== "undefined" ? (
                    <>
                      <br />
                      <br />
                      <TWrapper forcedValue={gt("youSave")}>
                        <InterpolateLinearlyReact
                          translationStringWithInterpolatedValues={gt("youSave").value}
                          args={[`${discountPercentage}%`]}
                        />
                      </TWrapper>
                    </>
                  ) : null}

                  {lowestPrice30Days ? (
                    <>
                      <br />
                      <br />
                      <TWrapper forcedValue={gt("lowestPriceLast30Days")}>
                        <InterpolateLinearlyReact
                          translationStringWithInterpolatedValues={gt("lowestPriceLast30Days").value}
                          args={[showPrice(lowestPrice30Days)]}
                        />
                      </TWrapper>
                    </>
                  ) : null}
                </>
              }
            />
          </div>
        );
      } else if (variant.discountedPrice === "discount-calculated-at-cart") {
        return <T>{pdpT("discountCalculatedAndAppliedInCart")}</T>;
      }

      return undefined;
    }

    const noteValue = getNoteValue();

    return {
      mainLabel: showDiscountablePriceStr(variant),
      noteLabel: noteValue,
    };
  };
};
